import { FormPage, FormSection } from '../components/ui/FormPage';
import { Badge, Button, Checkbox, Dialog, Field, Input, Select } from '@cloudflare/kumo';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAppToast } from '../hooks/useAppToast';
import { AppTabs as Tabs } from '../components/ui/AppTabs';
import { ICONS } from '../components/ui/icons';
import { SearchField } from '../components/ui/SearchField';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { SystemBackup } from '../components/SystemBackup';
import { PlatformEmailCard } from '../components/PlatformEmailCard';
import { StripeCard } from '../components/StripeCard';
import { OAuthProvidersCard } from '../components/OAuthProvidersCard';
import { MetaAppCard } from '../components/MetaAppCard';
import { AiProvidersCard } from '../components/AiProvidersCard';
import { VoiceProviderCard } from '../components/VoiceProviderCard';
import { DecisionModelCard } from '../components/DecisionModelCard';
import { SuperAdminDashboard } from './SuperAdminDashboard';
import { PlansPage } from './PlansPage';
import { useSearchParams } from 'react-router-dom';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  domain?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    contacts: number;
    interactiveCampaigns: number;
    whatsappSessions: number;
  };
  quota?: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
  };
}

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
  atualizadoEm: string;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    active: boolean;
  } | null;
}

interface TenantFormData {
  name: string;
  adminUser: {
    nome: string;
    email: string;
    senha: string;
  };
  quotas: {
    maxUsers: string;
    maxContacts: string;
    maxCampaigns: string;
    maxConnections: string;
  };
}

interface UserFormData {
  nome: string;
  email: string;
  senha: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'USER';
  tenantId?: string; // Mantido para compatibilidade
  tenantIds?: string[]; // Novo: múltiplos tenants
  ativo: boolean;
}




const PLATFORM_TABS = [
  { key: 'overview', slug: 'visao-geral', label: 'Visão geral' },
  { key: 'tenants', slug: 'empresas', label: 'Empresas' },
  { key: 'users', slug: 'usuarios', label: 'Usuários' },
  { key: 'plans', slug: 'planos', label: 'Planos' },
  { key: 'integrations', slug: 'integracoes', label: 'Integrações' },
  { key: 'backup', slug: 'backup', label: 'Backup' },
] as const;
type PlatformTab = typeof PLATFORM_TABS[number]['key'];

export function SuperAdminManagerPage() {
  const toast = useAppToast();
  // Abas da Plataforma; a aba ativa fica na URL (?aba=empresas) para links diretos
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: PlatformTab = PLATFORM_TABS.find((t) => t.slug === searchParams.get('aba'))?.key ?? 'overview';
  const setActiveTab = (key: PlatformTab) =>
    setSearchParams({ aba: PLATFORM_TABS.find((t) => t.key === key)!.slug }, { replace: true });  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  // Busca e filtros das abas Empresas e Usuários
  const [tenantSearch, setTenantSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userTenantFilter, setUserTenantFilter] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    adminUser: {
      nome: '',
      email: '',
      senha: ''
    },
    quotas: {
      maxUsers: '10',
      maxContacts: '1000',
      maxCampaigns: '50',
      maxConnections: '5'
    }
  });
  const [userFormData, setUserFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    senha: '',
    role: 'USER',
    tenantId: '',
    tenantIds: [],
    ativo: true
  });
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');

  // Exclusão pendente de confirmação (empresa exige digitar o nome)
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [impTarget, setImpTarget] = useState<User | null>(null);
  const [impReason, setImpReason] = useState('');
  const [impBusy, setImpBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ kind: 'tenant'; tenant: Tenant } | { kind: 'user'; user: User } | null>(null);
  useEffect(() => {
    loadData();
  }, [activeTab]);
  const loadData = async () => {
    // Só Empresas e Usuários carregam aqui; as outras abas cuidam dos próprios dados
    if (activeTab !== 'tenants' && activeTab !== 'users') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');

      if (activeTab === 'tenants') {
        const response = await fetch('/api/tenants', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || []);
        }
      } else if (activeTab === 'users') {
        // Usar endpoint global para SuperAdmin (visão de todos os usuários)
        const response = await fetch('/api/users/global', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.data?.users || []);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTenantStatus = async (tenant: Tenant) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...tenant,
          active: !tenant.active
        })
      });

      if (response.ok) {
        toast.success(`Empresa ${!tenant.active ? 'ativada' : 'desativada'} com sucesso!`);
        loadData();
      } else {
        throw new Error('Erro ao alterar status da empresa');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCreateTenant = () => {
    setEditingTenant(null);
    setFormData({
      name: '',
      adminUser: {
        nome: '',
        email: '',
        senha: ''
      },
      quotas: {
        maxUsers: '10',
        maxContacts: '1000',
        maxCampaigns: '50',
        maxConnections: '5'
      }
    });
    setIsModalOpen(true);
  };

  const handleEditTenant = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      adminUser: {
        nome: '',
        email: '',
        senha: ''
      },
      quotas: {
        maxUsers: tenant.quota?.maxUsers?.toString() || '10',
        maxContacts: tenant.quota?.maxContacts?.toString() || '1000',
        maxCampaigns: tenant.quota?.maxCampaigns?.toString() || '50',
        maxConnections: tenant.quota?.maxConnections?.toString() || '5'
      }
    });
    setIsModalOpen(true);
  };

  const handleDeleteTenant = async (tenant: Tenant) => {

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Tenant excluído com sucesso!');
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir empresa');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSubmitTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!editingTenant && (!formData.adminUser.nome || !formData.adminUser.email || !formData.adminUser.senha))) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingTenant ? `/api/tenants/${editingTenant.id}` : '/api/tenants';
      const method = editingTenant ? 'PUT' : 'POST';

      const body: any = {
        name: formData.name,
        quotas: formData.quotas
      };

      if (!editingTenant) {
        body.adminUser = formData.adminUser;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Empresa ${editingTenant ? 'atualizada' : 'criada'} com sucesso!`);
        setIsModalOpen(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || `Erro ao ${editingTenant ? 'atualizar' : 'criar'} empresa`);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const filteredUsers = users.filter((u) => {
    const term = userSearch.trim().toLowerCase();
    if (term && !`${u.nome} ${u.email}`.toLowerCase().includes(term)) return false;
    if (userTenantFilter === '__none__' ? !!u.tenant : userTenantFilter && u.tenant?.id !== userTenantFilter) return false;
    if (userRoleFilter && u.role !== userRoleFilter) return false;
    return true;
  });

  const getUsagePercentage = (current: number, max: number) => {
    return Math.round((current / max) * 100);
  };

  // Opções dos filtros e do papel (o `Select` do Cloudscape recebe a lista por prop)
  const roleOptions = [
    { value: 'USER', label: 'Usuário' },
    { value: 'ADMIN', label: 'Administrador' },
    { value: 'SUPERADMIN', label: 'Super Administrador (acesso global)' },
  ];
  const tenantFilterOptions = [
    { value: '__all__', label: 'Todas as empresas' },
    { value: '__none__', label: 'Sistema (sem empresa)' },
    ...Array.from(new Map(users.filter((u) => u.tenant).map((u) => [u.tenant!.id, u.tenant!.name])).entries())
      .map(([id, name]) => ({ value: id, label: name })),
  ];
  const roleFilterOptions = [
    { value: '__all__', label: 'Todos os papéis' },
    { value: 'SUPERADMIN', label: 'Superadmin' },
    { value: 'ADMIN', label: 'Administrador' },
    { value: 'USER', label: 'Usuário' },
  ];


  // User management functions
  const loadAvailableTenants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/users/tenants', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTenants(data.data?.tenants || []);
      }
    } catch {
      // falha silenciosa ao carregar empresas disponíveis
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setTenantSearchQuery(''); // Limpar busca ao criar novo usuário
    setUserFormData({
      nome: '',
      email: '',
      senha: '',
      role: 'USER',
      tenantId: '',
      tenantIds: [],
      ativo: true
    });
    loadAvailableTenants();
    setIsUserModalOpen(true);
  };

  const handleEditUser = async (user: User) => {
    setEditingUser(user);
    setTenantSearchQuery(''); // Limpar busca ao editar usuário

    // Carregar tenants do usuário via novo endpoint
    let userTenantIds: string[] = [];
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/user-tenants/users/${user.id}/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        userTenantIds = (data.tenants || []).map((t: any) => t.id);
      }
    } catch {
      // falha silenciosa ao carregar empresas do usuário
    }

    setUserFormData({
      nome: user.nome,
      email: user.email,
      senha: '',
      role: user.role as 'SUPERADMIN' | 'ADMIN' | 'USER',
      tenantId: user.tenant?.id || '',
      tenantIds: userTenantIds,
      ativo: user.ativo
    });
    loadAvailableTenants();
    setIsUserModalOpen(true);
  };

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userFormData.nome || !userFormData.email || (!editingUser && !userFormData.senha)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      const method = editingUser ? 'PUT' : 'POST';

      const body: any = {
        nome: userFormData.nome,
        email: userFormData.email,
        role: userFormData.role,
        ativo: userFormData.ativo
      };

      // Only send password if it's provided
      if (userFormData.senha) {
        body.senha = userFormData.senha;
      }

      // Handle tenant assignment based on role
      // Para SUPERADMIN, não enviamos tenantId (será null no backend)
      // Para outros roles, enviamos o primeiro tenant selecionado como compatibilidade
      if (userFormData.role !== 'SUPERADMIN' && userFormData.tenantIds && userFormData.tenantIds.length > 0) {
        body.tenantId = userFormData.tenantIds[0];
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();
        const userId = editingUser ? editingUser.id : result.data?.user?.id;

        // Se tem tenants selecionados, criar associações (para qualquer role, incluindo SUPERADMIN)
        if (userFormData.tenantIds && userFormData.tenantIds.length > 0 && userId) {
          // Se estamos editando, primeiro remover associações antigas
          if (editingUser) {
            try {
              // Buscar associações existentes
              const existingAssociationsResponse = await fetch(`/api/user-tenants`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (existingAssociationsResponse.ok) {
                const existingData = await existingAssociationsResponse.json();
                // Remover associações que não estão mais selecionadas
                for (const association of existingData.tenants || []) {
                  if (!userFormData.tenantIds.includes(association.id)) {
                    try {
                      await fetch(`/api/user-tenants/associations/${userId}/${association.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                    } catch (err) {
                      console.warn('Erro ao remover associação:', association.id, err);
                    }
                  }
                }
              }
            } catch (err) {
              console.warn('Erro ao buscar/remover associações antigas:', err);
            }
          }

          // Criar associações para todos os tenants selecionados
          for (const tenantId of userFormData.tenantIds) {
            try {
              const associationResponse = await fetch('/api/user-tenants/associations', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  userId,
                  tenantId,
                  role: userFormData.role
                })
              });

              // Se der erro 400 (já existe), não é problema
              if (!associationResponse.ok && associationResponse.status !== 400) {
                console.warn('Erro ao criar associação para tenant:', tenantId);
              }
            } catch (err) {
              console.warn('Erro ao criar associação para tenant:', tenantId, err);
            }
          }
        }

        toast.success(`Usuário ${editingUser ? 'atualizado' : 'criado'} com sucesso!`);
        setIsUserModalOpen(false);
        loadData();
      } else {
        const error = await response.json();

        // Verificar se é erro de quota
        if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
          toast.warning(
            error.message || 'Limite de usuários atingido.',
            'Faça upgrade do plano para continuar.'
          );
          return;
        }

        throw new Error(error.message || `Erro ao ${editingUser ? 'atualizar' : 'criar'} usuário`);
      }
    } catch (error: any) {
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error.message && error.message.includes('Limite'))) {
        toast.error(error.message);
      }
    }
  };

  const handleImpersonate = async () => {
    if (!impTarget) return;
    setImpBusy(true);
    try {
      const response = await fetch('/api/platform/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ userId: impTarget.id, reason: impReason.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Não foi possível entrar como o usuário');
      await startImpersonation(data.token, data.user, data.expiresInMinutes);
      setImpTarget(null);
      setImpReason('');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao personificar');
    } finally {
      setImpBusy(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ativo: !user.ativo
        })
      });

      if (response.ok) {
        toast.success(`Usuário ${!user.ativo ? 'ativado' : 'desativado'} com sucesso!`);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao alterar status do usuário');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (user: User) => {

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Usuário excluído com sucesso!');
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir usuário');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '28px 40px 0', marginBottom: 24 }}>
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Plataforma</h2>
            <div style={{ color: 'var(--text-muted)' }}>Administração de todas as organizações</div>
          </div>
        </div>
        <div className="page-content">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2].map(i => (
              <div key={i} style={{ height: i === 1 ? 32 : 240, background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--border)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }


  const renderPlatformTabContent = (key: PlatformTab) => {
    if (key === 'overview') return <SuperAdminDashboard embedded />;
    if (key === 'plans') return <PlansPage embedded />;
    if (key === 'backup') return <SystemBackup />;
    if (key === 'tenants') {
        const term = tenantSearch.trim().toLowerCase();
        const shown = tenants.filter((t) => !term || t.name.toLowerCase().includes(term) || t.slug.toLowerCase().includes(term));
      return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-full sm:min-w-[320px]">
                <SearchField ariaLabel="Buscar empresas" placeholder="Buscar por nome ou identificador..." value={tenantSearch} onChange={setTenantSearch} />
              </div>
              <Button variant="primary" icon={ICONS.Plus} onClick={handleCreateTenant}>
                Criar empresa
              </Button>
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              {term ? `${shown.length} de ${tenants.length}` : tenants.length} {tenants.length === 1 ? 'empresa' : 'empresas'}
            </div>

            {shown.length === 0 ? (
              <div className="connection-empty">
                <ICONS.Building size={24} />
                <h4 className="text-base font-semibold">{tenants.length === 0 ? 'Nenhuma empresa cadastrada' : 'Nenhuma empresa encontrada'}</h4>
              </div>
            ) : (
              <div className="tenant-list">
                {shown.map((tenant) => (
                  <div key={tenant.id} className="tenant-row">
                    <div style={{ minWidth: 200 }}>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold" style={{ fontSize: 15 }}>{tenant.name}</div>
                        <Badge variant={tenant.active ? 'green' : 'red'}>{tenant.active ? 'Ativa' : 'Inativa'}</Badge>
                      </div>
                      <div style={{ color: 'var(--text-faint)', fontSize: 12, fontFamily: 'Source Code Pro, monospace' }}>{tenant.slug}</div>
                    </div>

                    {tenant._count && tenant.quota && (
                      <div className="tenant-usage">
                        {[
                          { label: 'Usuários', current: tenant._count.users, max: tenant.quota.maxUsers },
                          { label: 'Contatos', current: tenant._count.contacts, max: tenant.quota.maxContacts },
                          { label: 'Campanhas interativas', current: tenant._count.interactiveCampaigns, max: tenant.quota.maxCampaigns },
                          { label: 'Conexões', current: tenant._count.whatsappSessions, max: tenant.quota.maxConnections },
                        ].map((stat) => {
                          const pct = getUsagePercentage(stat.current, stat.max);
                          const color = pct >= 90 ? 'var(--crm-text-error)' : pct >= 70 ? 'var(--crm-text-warning)' : 'var(--brand-text)';
                          return (
                            <div key={stat.label} className="tenant-usage__item" title={`${stat.current} de ${stat.max.toLocaleString('pt-BR')} (${pct}%)`}>
                              <div className="flex justify-between gap-2">
                                <span>{stat.label}</span>
                                <span className="tenant-usage__value">{stat.current.toLocaleString('pt-BR')}<span> / {stat.max.toLocaleString('pt-BR')}</span></span>
                              </div>
                              <div className="tenant-usage__bar"><div style={{ width: `${Math.max(pct, stat.current > 0 ? 2 : 0)}%`, background: color }} /></div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="secondary" onClick={() => handleEditTenant(tenant)}>Editar</Button>
                      <Button variant="ghost" shape="square" icon={tenant.active ? ICONS.Pause : ICONS.Play} aria-label={`${tenant.active ? 'Desativar' : 'Ativar'} ${tenant.name}`} title={tenant.active ? 'Desativar empresa' : 'Ativar empresa'} onClick={() => handleToggleTenantStatus(tenant)} />
                      <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Excluir ${tenant.name}`} title={'Excluir empresa'} onClick={() => setPendingDelete({ kind: 'tenant', tenant })} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
      );
    }
    if (key === 'users') {
      return (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h4 className="text-base font-semibold">Gerenciar Usuários</h4>
              <p className="text-sm text-gray-600 mt-1">Visualização e gerenciamento global de todos os usuários do sistema</p>
            </div>
            <Button variant="primary" icon={ICONS.Plus} onClick={handleCreateUser}>
              Adicionar usuário
            </Button>
          </div>
          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap gap-3">
            <div className="flex-1 min-w-full sm:min-w-[280px]">
              <SearchField ariaLabel="Buscar usuários" placeholder="Buscar por nome ou e-mail..." value={userSearch} onChange={setUserSearch} />
            </div>
            <div className="w-full sm:w-52">
              <Select value={(tenantFilterOptions.find((o) => o.value === (userTenantFilter || '__all__')) ?? null)?.value ?? null} items={tenantFilterOptions} onValueChange={(value: string | null) => setUserTenantFilter(value === '__all__' ? '' : (value ?? ''))} aria-label="Filtrar por empresa" placeholder="Todas as empresas" />
            </div>
            <div className="w-full sm:w-44">
              <Select value={(roleFilterOptions.find((o) => o.value === (userRoleFilter || '__all__')) ?? null)?.value ?? null} items={roleFilterOptions} onValueChange={(value: string | null) => setUserRoleFilter(value === '__all__' ? '' : (value ?? ''))} aria-label="Filtrar por papel" placeholder="Todos os papéis" />
            </div>
            {(userSearch || userTenantFilter || userRoleFilter) && (
              <Button variant="secondary" onClick={() => { setUserSearch(''); setUserTenantFilter(''); setUserRoleFilter(''); }}>Limpar filtros</Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Papel</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Último login</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado em</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.nome}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {user.tenant ? (
                        <div>
                          <div className="text-sm text-gray-900">{user.tenant.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{user.tenant.slug}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Sistema</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={user.role === 'SUPERADMIN' ? 'neutral'
                          : user.role === 'ADMIN' ? 'blue'
                          : 'neutral'}>
                        {user.role === 'SUPERADMIN' ? 'Superadmin' :
                         user.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={user.ativo ? 'green' : 'red'}>
                        {user.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {user.ultimoLogin
                        ? new Date(user.ultimoLogin).toLocaleDateString('pt-BR')
                        : 'Nunca'
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.criadoEm).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" shape="square" icon={ICONS.Edit} aria-label={`Editar ${user.nome}`} title={'Editar'} onClick={() => handleEditUser(user)} />
                        <Button variant="ghost" shape="square" icon={user.ativo ? ICONS.Lock : ICONS.Unlock} aria-label={`${user.ativo ? 'Desativar' : 'Ativar'} ${user.nome}`} title={user.ativo ? 'Desativar' : 'Ativar'} onClick={() => handleToggleUserStatus(user)} />
                        {user.role !== 'SUPERADMIN' && user.ativo && (
                          <Button variant="ghost" shape="square" icon={ICONS.Visibility} aria-label={`Acessar como ${user.nome}`} title="Acessar como este usuário" onClick={() => { setImpReason(''); setImpTarget(user); }} />
                        )}
                        {user.role !== 'SUPERADMIN' && (
                          <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label={`Excluir ${user.nome}`} title={'Excluir'} onClick={() => setPendingDelete({ kind: 'user', user })} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">{users.length === 0 ? 'Nenhum usuário cadastrado' : 'Nenhum usuário corresponde à busca e aos filtros'}</p>
              </div>
            )}
          </div>
        </div>
      );
    }
    if (key === 'integrations') {
      return (
        <div className="space-y-4">
          <div style={{ color: 'var(--text-muted)' }}>
            App da Meta (WhatsApp oficial e Instagram), e-mail, cobrança, voz (ElevenLabs) e login usados por todas as organizações. As chaves ficam só no servidor.
          </div>
          <div className="settings-grid">
            <PlatformEmailCard />
            <StripeCard />
            <OAuthProvidersCard />
            <MetaAppCard />
            <AiProvidersCard />
            <VoiceProviderCard />
            <DecisionModelCard />
          </div>
        </div>
      );
    }
    return null;
  };
  if (isModalOpen) {
    return (
      <FormPage
        backTo="/super-admin?aba=empresas"
        backLabel="Plataforma"
        onBack={() => setIsModalOpen(false)}
        title={editingTenant ? 'Editar empresa' : 'Nova empresa'}
        description="Dados, plano e limites da organização."
        formId="tenant-form"
        submitLabel={editingTenant ? 'Atualizar empresa' : 'Criar empresa'}
        onCancel={() => setIsModalOpen(false)}
      >
        <form id="tenant-form" onSubmit={handleSubmitTenant}>
          <FormSection title="Dados da empresa">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                {/* Coluna Esquerda — Dados da Empresa e Admin */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <ICONS.Building size={16} />
                      <div className="font-semibold">Dados da Empresa</div>
                    </div>
                    <Field label="Nome da Empresa *" description="O identificador único será gerado automaticamente">
                      <Input onChange={(e) => setFormData({ ...formData, name: e.target.value })} value={formData.name} placeholder="Nome da empresa" />
                    </Field>
                  </div>

                  {!editingTenant && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                        <ICONS.Person size={16} />
                        <div className="font-semibold">Usuário Administrador</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Field label="Nome do Admin *">
                          <Input onChange={(e) => setFormData({
                              ...formData,
                              adminUser: { ...formData.adminUser, nome: e.target.value }
                            })} value={formData.adminUser.nome} placeholder="Nome completo" />
                        </Field>
                        <Field label="E-mail do Admin *">
                          <Input onChange={(e) => setFormData({
                              ...formData,
                              adminUser: { ...formData.adminUser, email: e.target.value }
                            })} type="email" value={formData.adminUser.email} placeholder="admin@exemplo.com" />
                        </Field>
                        <Field label="Senha do Admin *">
                          <Input onChange={(e) => setFormData({
                              ...formData,
                              adminUser: { ...formData.adminUser, senha: e.target.value }
                            })} type="password" value={formData.adminUser.senha} placeholder="Mínimo 6 caracteres" />
                        </Field>
                      </div>
                    </div>
                  )}
                </div>

                {/* Coluna Direita — Limites e Provedores */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <ICONS.Charts size={16} />
                      <div className="font-semibold">Limites e Quotas</div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {[
                        { label: 'Máx. Usuários *', key: 'maxUsers' },
                        { label: 'Máx. Conexões *', key: 'maxConnections' },
                        { label: 'Máx. Contatos *', key: 'maxContacts' },
                        { label: 'Máx. campanhas interativas *', key: 'maxCampaigns' },
                      ].map(({ label, key }) => (
                        <Field label={label} key={key}>
                          <Input onChange={(e) => setFormData({
                              ...formData,
                              quotas: { ...formData.quotas, [key]: e.target.value }
                            })} value={formData.quotas[key as keyof typeof formData.quotas]} />
                        </Field>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              
          </FormSection>
        </form>
      </FormPage>
    );
  }

  if (isUserModalOpen) {
    return (
      <FormPage
        backTo="/super-admin?aba=empresas"
        backLabel="Plataforma"
        onBack={() => setIsUserModalOpen(false)}
        title={editingUser ? 'Editar usuário' : 'Novo usuário'}
        description="Conta, papel e empresa do usuário."
        formId="user-form-sa"
        submitLabel={editingUser ? 'Atualizar usuário' : 'Criar usuário'}
        onCancel={() => setIsUserModalOpen(false)}
      >
        <form id="user-form-sa" onSubmit={handleSubmitUser}>
          <FormSection title="Dados do usuário">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Field label="Nome Completo *">
                  <Input onChange={(e) => setUserFormData({ ...userFormData, nome: e.target.value })} value={userFormData.nome} placeholder="Nome completo do usuário" />
                </Field>

                <Field label="E-mail *">
                  <Input onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })} type="email" value={userFormData.email} placeholder="email@exemplo.com" />
                </Field>

                <Field label={editingUser ? 'Senha (deixe vazio para manter)' : 'Senha *'} description="Mínimo 6 caracteres, uma maiúscula, uma minúscula e um número">
                  <Input onChange={(e) => setUserFormData({ ...userFormData, senha: e.target.value })} type="password" value={userFormData.senha} placeholder="Senha segura" />
                </Field>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <ICONS.Person size={16} />
                    <div className="font-semibold">Nível de Acesso *</div>
                  </div>
                  <Select value={(roleOptions.find((o) => o.value === userFormData.role) ?? null)?.value ?? null} items={roleOptions} onValueChange={(value: string | null) => setUserFormData({ ...userFormData, role: value as 'SUPERADMIN' | 'ADMIN' | 'USER' })} />
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
                    {userFormData.role === 'SUPERADMIN'
                      ? 'Super Admin tem acesso a TODAS as empresas automaticamente'
                      : 'Defina o nível de acesso do usuário nas empresas selecionadas'}
                  </div>
                </div>

                <div>
                  <div className="font-semibold" style={{ marginBottom: 8 }}>
                    Empresas {userFormData.role === 'SUPERADMIN' ? '(Opcional)' : '*'}
                  </div>

                  {availableTenants.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Nenhuma empresa disponível</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <Field label="">
                        <Input onChange={(e) => setTenantSearchQuery(e.target.value)} placeholder="Buscar empresa..." value={tenantSearchQuery} />
                      </Field>

                      <div style={{ border: '1px solid var(--border)', borderRadius: 4, padding: 8, maxHeight: 220, overflowY: 'auto', background: 'var(--surface)' }}>
                        {(() => {
                          const filteredTenants = availableTenants.filter(t =>
                            t.name.toLowerCase().includes(tenantSearchQuery.toLowerCase()) ||
                            t.slug.toLowerCase().includes(tenantSearchQuery.toLowerCase())
                          );

                          if (filteredTenants.length === 0) {
                            return (
                              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                                Nenhuma empresa encontrada
                              </div>
                            );
                          }

                          return filteredTenants.map((tenant) => {
                            const isSelected = (userFormData.tenantIds || []).includes(tenant.id);
                            return (
                              <div
                                key={tenant.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '8px 10px',
                                  borderRadius: 4,
                                  marginBottom: 4,
                                  background: isSelected ? 'var(--tint-green)' : 'var(--surface-2)',
                                  border: `1px solid ${isSelected ? '#C3E6CB' : 'var(--border)'}`,
                                }}
                              >
                                <Checkbox onCheckedChange={(checked: boolean) => {
                                    const tenantIds = userFormData.tenantIds || [];
                                    if (checked) {
                                      setUserFormData({ ...userFormData, tenantIds: [...tenantIds, tenant.id] });
                                    } else {
                                      setUserFormData({ ...userFormData, tenantIds: tenantIds.filter(id => id !== tenant.id) });
                                    }
                                  }} checked={isSelected} label={<>
                                  {tenant.name}
                                </>} />
                                <Badge variant={tenant.active ? 'green' : 'red'}>
                                  {tenant.active ? 'Ativa' : 'Inativa'}
                                </Badge>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                    {userFormData.role === 'SUPERADMIN'
                      ? 'Deixe vazio para acesso global a todas as empresas'
                      : 'Selecione pelo menos uma empresa'}
                    {(userFormData.tenantIds || []).length > 0 && (
                      <span style={{ color: 'var(--brand-text)', marginLeft: 8, fontWeight: 600 }}>
                        · {(userFormData.tenantIds || []).length} selecionada{(userFormData.tenantIds || []).length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <Checkbox onCheckedChange={(checked: boolean) => setUserFormData({ ...userFormData, ativo: checked })} checked={userFormData.ativo} label={<>
                  Usuário ativo
                </>} />
              </div>

              
          </FormSection>
        </form>
      </FormPage>
    );
  }

  return (
    <>
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: '28px 40px 0', marginBottom: 24 }}>
      <div>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Plataforma</h2>
        <div style={{ color: 'var(--text-muted)' }}>Administração de todas as organizações</div>
      </div>
    </div>
    <div className="page-content">
      {/* Abas da Plataforma (Cloudscape) */}
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId as PlatformTab)}
        tabs={PLATFORM_TABS.map((t) => ({ id: t.key, label: t.label, content: renderPlatformTabContent(t.key) }))}
      />

      <Dialog.Root open={!!impTarget} onOpenChange={(o: boolean) => { if (!o && !impBusy) setImpTarget(null); }}>
        <Dialog size="base" className="p-6">
          <Dialog.Title className="mb-2 text-lg font-semibold">Acessar como {impTarget?.nome}</Dialog.Title>
          <p className="mb-4 text-sm text-kumo-subtle">
            Você entra na conta de {impTarget?.email} por 1 hora. O acesso é registrado na auditoria da organização, e senha, 2FA e tokens ficam bloqueados.
          </p>
          <Field label="Motivo (obrigatório)" description="Ex.: cliente reportou erro ao enviar campanha. Fica gravado na auditoria.">
            <Input value={impReason} onChange={(e) => setImpReason(e.target.value)} maxLength={300} autoFocus />
          </Field>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" disabled={impBusy} onClick={() => setImpTarget(null)}>Cancelar</Button>
            <Button variant="primary" loading={impBusy} disabled={impReason.trim().length < 10} onClick={() => { void handleImpersonate(); }}>Entrar como usuário</Button>
          </div>
        </Dialog>
      </Dialog.Root>

      <DangerConfirmModal
        open={pendingDelete !== null}
        title={pendingDelete?.kind === 'tenant' ? `Excluir a empresa "${pendingDelete.tenant.name}"?` : `Excluir o usuário ${pendingDelete?.kind === 'user' ? pendingDelete.user.nome : ''}?`}
        confirmText="Excluir"
        objectName={pendingDelete?.kind === 'tenant' ? pendingDelete.tenant.name : pendingDelete?.kind === 'user' ? pendingDelete.user.nome : undefined}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target?.kind === 'tenant') handleDeleteTenant(target.tenant);
          else if (target?.kind === 'user') handleDeleteUser(target.user);
        }}
      >
        {pendingDelete?.kind === 'tenant'
          ? 'Apaga a empresa e, junto com ela, todos os usuários, contatos, campanhas e conexões WhatsApp. Não dá para desfazer.'
          : 'O usuário perde o acesso e é removido. Não dá para desfazer.'}
      </DangerConfirmModal>

      {/* Modal for Create/Edit Tenant */}


      {/* Modal for Create/Edit User */}

    </div>
    </>
  );
}
